import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {FormatFunctionRegistryService} from '../../../service/api/format-function-registry.service';
import {
    FormatFunctionDefinitionDto,
    FormatParameterDto
} from '../../../model/dto/api/format-function-definition.dto';
import {FormatPreviewResponseDto} from '../../../model/dto/api/format-preview-response.dto';

/**
 * Recursion-depth limit for nested format-function expressions. Matches the
 * backend executor guard described in the requirement spec.
 *
 * @ticket TM-1000001726
 */
const MAX_NESTING_DEPTH = 32;

/**
 * Visible label of the variable-mode toggle for a parameter.
 *
 * @ticket TM-1000001726
 */
type ParameterValueMode = 'literal' | 'variable' | 'nested';

/**
 * UI-side editor-state for a single parameter. The actual script value is
 * derived from this on every serialization pass.
 *
 * @ticket TM-1000001726
 */
interface ParameterEditorState {
    /** Parameter metadata from the function definition. */
    definition: FormatParameterDto;
    /** Currently selected entry mode. */
    mode: ParameterValueMode;
    /** Literal value (string/number/boolean/etc). */
    literalValue: unknown;
    /** Variable name without the `$$` prefix. */
    variableName: string;
    /** Nested function-call editor state. */
    nestedFunctionName?: string;
    nestedParameters?: ParameterEditorState[];
}

/**
 * Option entry in the function dropdown. Grouped per category.
 *
 * @ticket TM-1000001726
 */
interface FunctionOptionGroup {
    label: string;
    items: { label: string; value: string }[];
}

/**
 * Structured editor used when the surrounding {@code Logic} uses
 * {@link LogicLanguageEnum.format}. Replaces the free-form code editor with a
 * function selector, a parameter editor, and a live-preview panel.
 *
 * Inputs:
 * - {@code script}     – existing JSON script (may be empty).
 * - {@code variables}  – list of import variable names available for `$$varName` references.
 *
 * Outputs:
 * - {@code scriptChange} – emits the serialized JSON whenever the user mutates the form.
 *
 * @ticket TM-1000001726
 */
@Component({
    selector: 'mvs-format-script-editor',
    templateUrl: './format-script-editor.component.html',
    styleUrls: ['./format-script-editor.component.scss'],
    standalone: false
})
export class FormatScriptEditorComponent implements OnInit, OnChanges, OnDestroy {

    /** Current JSON-script value (two-way bindable via {@code scriptChange}). */
    @Input() script: string | null | undefined;

    /** Variable names available as `$$varName` references. */
    @Input() variables: string[] = [];

    /** Optional preset test-values for live-preview. Keyed by variable name. */
    @Input() testVariableValues: Record<string, unknown> = {};

    /** Internal depth (1 for root). Used to enforce {@link MAX_NESTING_DEPTH}. */
    @Input() depth: number = 1;

    /** Reuse already-loaded definitions (passed to recursive nested instances). */
    @Input() preloadedDefinitions: FormatFunctionDefinitionDto[] | null = null;

    @Output() scriptChange: EventEmitter<string> = new EventEmitter<string>();

    initialized: boolean = false;

    /** All known format-function definitions indexed by upper-snake name. */
    definitions: FormatFunctionDefinitionDto[] = [];
    definitionByName: Map<string, FormatFunctionDefinitionDto> = new Map();

    /** Grouped & filtered dropdown items (PrimeNG p-select supports `group=true`). */
    functionGroups: FunctionOptionGroup[] = [];
    functionFilter: string = '';

    /** Currently chosen function (upper-snake name). */
    selectedFunctionName: string | null = null;
    activeDefinition: FormatFunctionDefinitionDto | null = null;

    /** Editor-state per parameter, mirrors {@link activeDefinition#parameters}. */
    parameterStates: ParameterEditorState[] = [];

    /** Live-preview state. */
    previewResult: unknown = null;
    previewError: string | null = null;
    previewLoading: boolean = false;
    previewBusy: boolean = false;
    /** Whether the preview can be auto-triggered (root component only). */
    previewEnabled: boolean = true;

    /** Parameter-type → which mode entries the user may toggle to. */
    readonly modeOptions: { label: string; value: ParameterValueMode }[] = [
        {label: 'Wert', value: 'literal'},
        {label: 'Variable', value: 'variable'},
        {label: 'Funktion', value: 'nested'}
    ];

    private destroy$: Subject<void> = new Subject<void>();
    private previewTrigger$: Subject<void> = new Subject<void>();

    constructor(private readonly registryService: FormatFunctionRegistryService) {
    }

    ngOnInit(): void {
        this.previewEnabled = this.depth === 1;
        if (this.depth > MAX_NESTING_DEPTH) {
            // Hard cap. The UI surfaces an inline message instead of rendering.
            this.initialized = true;
            return;
        }
        if (this.previewEnabled) {
            this.previewTrigger$
                .pipe(debounceTime(500), takeUntil(this.destroy$))
                .subscribe(() => this.executePreview());
        }
        this.loadDefinitions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['script'] && !changes['script'].firstChange) {
            this.hydrateFromScript();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ---------------------------------------------------------------------------------------------
    // Definitions loading
    // ---------------------------------------------------------------------------------------------

    /**
     * Loads function definitions from the backend (or reuses preloaded list)
     * and builds the grouped dropdown structure.
     */
    private loadDefinitions(): void {
        if (this.preloadedDefinitions && this.preloadedDefinitions.length > 0) {
            this.applyDefinitions(this.preloadedDefinitions);
            return;
        }
        this.registryService.listDefinitions().subscribe({
            next: defs => this.applyDefinitions(defs ?? []),
            error: () => {
                this.applyDefinitions([]);
                this.initialized = true;
            }
        });
    }

    private applyDefinitions(defs: FormatFunctionDefinitionDto[]): void {
        this.definitions = defs;
        this.definitionByName = new Map(defs.map(d => [d.name.toUpperCase(), d]));
        this.rebuildFunctionGroups();
        this.hydrateFromScript();
        this.initialized = true;
    }

    /**
     * Builds the grouped/filtered dropdown options.
     */
    rebuildFunctionGroups(): void {
        const filter = (this.functionFilter ?? '').trim().toLowerCase();
        const byCategory = new Map<string, { label: string; value: string }[]>();

        for (const def of this.definitions) {
            const matches = !filter
                || def.name.toLowerCase().includes(filter)
                || (def.label ?? '').toLowerCase().includes(filter)
                || (def.description ?? '').toLowerCase().includes(filter);
            if (!matches) {
                continue;
            }
            const cat = def.category || 'OTHER';
            if (!byCategory.has(cat)) {
                byCategory.set(cat, []);
            }
            byCategory.get(cat)!.push({label: def.label || def.name, value: def.name});
        }
        const groups: FunctionOptionGroup[] = [];
        for (const cat of Array.from(byCategory.keys()).sort()) {
            const items = byCategory.get(cat)!.sort((a, b) => a.label.localeCompare(b.label));
            groups.push({label: cat, items});
        }
        this.functionGroups = groups;
    }

    onFilterChange(filter: string): void {
        this.functionFilter = filter ?? '';
        this.rebuildFunctionGroups();
    }

    // ---------------------------------------------------------------------------------------------
    // Hydration / Serialization
    // ---------------------------------------------------------------------------------------------

    /**
     * Parses the current {@code script} input and populates the editor.
     */
    private hydrateFromScript(): void {
        if (!this.script || !this.script.trim()) {
            this.selectedFunctionName = null;
            this.activeDefinition = null;
            this.parameterStates = [];
            return;
        }
        try {
            const parsed = JSON.parse(this.script);
            this.applyParsedFunction(parsed);
        } catch (err) {
            // Invalid JSON — keep editor empty so the user can start fresh.
            this.selectedFunctionName = null;
            this.activeDefinition = null;
            this.parameterStates = [];
        }
    }

    private applyParsedFunction(parsed: unknown): void {
        if (!parsed || typeof parsed !== 'object' || !('function' in (parsed as object))) {
            return;
        }
        const obj = parsed as { function: string; parameters?: Record<string, unknown> };
        const name = (obj.function || '').toUpperCase();
        const def = this.definitionByName.get(name);
        if (!def) {
            this.selectedFunctionName = name;
            this.activeDefinition = null;
            this.parameterStates = [];
            return;
        }
        this.selectedFunctionName = def.name;
        this.activeDefinition = def;
        this.parameterStates = def.parameters.map(p =>
            this.buildParameterState(p, obj.parameters ? obj.parameters[p.name] : undefined)
        );
    }

    private buildParameterState(definition: FormatParameterDto, rawValue: unknown): ParameterEditorState {
        if (typeof rawValue === 'string' && rawValue.startsWith('$$')) {
            return {
                definition,
                mode: 'variable',
                literalValue: this.defaultLiteralFor(definition),
                variableName: rawValue.substring(2)
            };
        }
        if (rawValue && typeof rawValue === 'object' && 'function' in (rawValue as object)) {
            const nested = rawValue as { function: string; parameters?: Record<string, unknown> };
            const nestedDef = this.definitionByName.get((nested.function || '').toUpperCase());
            return {
                definition,
                mode: 'nested',
                literalValue: this.defaultLiteralFor(definition),
                variableName: '',
                nestedFunctionName: nested.function,
                nestedParameters: nestedDef
                    ? nestedDef.parameters.map(p =>
                        this.buildParameterState(p, nested.parameters ? nested.parameters[p.name] : undefined)
                    )
                    : []
            };
        }
        return {
            definition,
            mode: 'literal',
            literalValue: rawValue !== undefined ? rawValue : this.defaultLiteralFor(definition),
            variableName: ''
        };
    }

    private defaultLiteralFor(definition: FormatParameterDto): unknown {
        if (definition.defaultValue !== undefined && definition.defaultValue !== null) {
            return definition.defaultValue;
        }
        switch ((definition.type || '').toUpperCase()) {
            case 'NUMBER':
                return null;
            case 'BOOLEAN':
                return false;
            case 'LIST':
                return [];
            default:
                return '';
        }
    }

    /**
     * Serializes the current editor-state into the JSON-script representation and
     * emits it via {@code scriptChange}.
     */
    private emitScript(): void {
        if (!this.activeDefinition) {
            this.scriptChange.emit('');
            return;
        }
        const node = this.serializeStates(this.activeDefinition, this.parameterStates);
        const json = JSON.stringify(node);
        this.script = json;
        this.scriptChange.emit(json);
        this.schedulePreview();
    }

    private serializeStates(def: FormatFunctionDefinitionDto, states: ParameterEditorState[]): object {
        const params: Record<string, unknown> = {};
        for (const state of states) {
            const value = this.serializeState(state);
            if (value !== undefined) {
                params[state.definition.name] = value;
            }
        }
        return {function: def.name, parameters: params};
    }

    private serializeState(state: ParameterEditorState): unknown {
        if (state.mode === 'variable') {
            return state.variableName ? `$$${state.variableName}` : undefined;
        }
        if (state.mode === 'nested') {
            if (!state.nestedFunctionName) {
                return undefined;
            }
            const nestedDef = this.definitionByName.get(state.nestedFunctionName.toUpperCase());
            if (!nestedDef) {
                return undefined;
            }
            return this.serializeStates(nestedDef, state.nestedParameters ?? []);
        }
        return state.literalValue;
    }

    // ---------------------------------------------------------------------------------------------
    // Event handlers (template-bound)
    // ---------------------------------------------------------------------------------------------

    onFunctionSelected(name: string | null): void {
        this.selectedFunctionName = name;
        if (!name) {
            this.activeDefinition = null;
            this.parameterStates = [];
            this.emitScript();
            return;
        }
        const def = this.definitionByName.get(name.toUpperCase());
        if (!def) {
            this.activeDefinition = null;
            this.parameterStates = [];
            return;
        }
        this.activeDefinition = def;
        this.parameterStates = def.parameters.map(p =>
            this.buildParameterState(p, p.defaultValue)
        );
        this.emitScript();
    }

    onParameterModeChange(state: ParameterEditorState, mode: ParameterValueMode): void {
        state.mode = mode;
        if (mode === 'nested' && !state.nestedParameters) {
            state.nestedParameters = [];
        }
        this.emitScript();
    }

    onLiteralChange(state: ParameterEditorState, value: unknown): void {
        state.literalValue = value;
        this.emitScript();
    }

    onVariableChange(state: ParameterEditorState, name: string): void {
        state.variableName = name;
        this.emitScript();
    }

    onNestedFunctionChange(state: ParameterEditorState, name: string | null): void {
        state.nestedFunctionName = name ?? undefined;
        if (name) {
            const nestedDef = this.definitionByName.get(name.toUpperCase());
            state.nestedParameters = nestedDef
                ? nestedDef.parameters.map(p => this.buildParameterState(p, p.defaultValue))
                : [];
        } else {
            state.nestedParameters = [];
        }
        this.emitScript();
    }

    /**
     * Forwarded from a recursive nested editor instance. Updates the nested
     * script segment and re-emits the full root script.
     */
    onNestedScriptChange(state: ParameterEditorState, nestedJson: string): void {
        if (!nestedJson) {
            state.nestedFunctionName = undefined;
            state.nestedParameters = [];
            this.emitScript();
            return;
        }
        try {
            const parsed = JSON.parse(nestedJson) as { function: string };
            state.nestedFunctionName = parsed.function;
            // The nested editor manages its own parameterStates; we simply trigger re-serialization
            // on the next emission cycle by re-parsing it through buildParameterState.
            const nestedDef = this.definitionByName.get((parsed.function || '').toUpperCase());
            if (nestedDef) {
                state.nestedParameters = nestedDef.parameters.map(p =>
                    this.buildParameterState(p, (parsed as any).parameters?.[p.name])
                );
            }
        } catch {
            // ignore; nested editor will re-emit
        }
        this.emitScript();
    }

    // ---------------------------------------------------------------------------------------------
    // Preview
    // ---------------------------------------------------------------------------------------------

    private schedulePreview(): void {
        if (!this.previewEnabled) {
            return;
        }
        if (!this.areMandatoryParametersFilled()) {
            this.previewResult = null;
            this.previewError = null;
            return;
        }
        this.previewBusy = true;
        this.previewTrigger$.next();
    }

    /**
     * Returns true when all root-level required parameters are populated.
     */
    areMandatoryParametersFilled(): boolean {
        if (!this.activeDefinition) {
            return false;
        }
        return this.parameterStates.every(state => this.isStateFilled(state));
    }

    private isStateFilled(state: ParameterEditorState): boolean {
        if (!state.definition.required) {
            return true;
        }
        switch (state.mode) {
            case 'literal':
                return state.literalValue !== null
                    && state.literalValue !== undefined
                    && state.literalValue !== '';
            case 'variable':
                return !!state.variableName;
            case 'nested':
                return !!state.nestedFunctionName
                    && (state.nestedParameters ?? []).every(s => this.isStateFilled(s));
        }
    }

    /**
     * Manually triggers a preview request — bound to the "Vorschau" button.
     */
    runPreviewManually(): void {
        if (!this.activeDefinition) {
            return;
        }
        this.executePreview();
    }

    private executePreview(): void {
        if (!this.script) {
            this.previewBusy = false;
            return;
        }
        this.previewLoading = true;
        this.previewBusy = true;
        this.registryService.preview({
            script: this.script,
            variables: this.testVariableValues
        }).subscribe({
            next: (response: FormatPreviewResponseDto) => {
                this.previewResult = response.result ?? null;
                this.previewError = response.error ?? null;
                this.previewLoading = false;
                this.previewBusy = false;
            },
            error: err => {
                this.previewResult = null;
                this.previewError = err?.error?.message || err?.message || 'Vorschau fehlgeschlagen';
                this.previewLoading = false;
                this.previewBusy = false;
            }
        });
    }

    onTestVariableValueChange(variableName: string, value: unknown): void {
        this.testVariableValues = {...this.testVariableValues, [variableName]: value};
        this.schedulePreview();
    }

    // ---------------------------------------------------------------------------------------------
    // Template helpers
    // ---------------------------------------------------------------------------------------------

    /**
     * Returns the list of variable names used by the script — surfaced as
     * extra input fields in the test-values panel.
     */
    get referencedVariableNames(): string[] {
        const names = new Set<string>();
        this.collectVariableNames(this.parameterStates, names);
        return Array.from(names).sort();
    }

    private collectVariableNames(states: ParameterEditorState[], target: Set<string>): void {
        for (const state of states) {
            if (state.mode === 'variable' && state.variableName) {
                target.add(state.variableName);
            }
            if (state.mode === 'nested' && state.nestedParameters) {
                this.collectVariableNames(state.nestedParameters, target);
            }
        }
    }

    /**
     * Convenience getter — true when the user-driven recursion exceeded the limit.
     */
    get exceededDepth(): boolean {
        return this.depth > MAX_NESTING_DEPTH;
    }

    /**
     * Serializes a nested parameter state into a JSON script for the recursive
     * editor child component.
     */
    serializeNested(state: ParameterEditorState): string {
        if (!state.nestedFunctionName) {
            return '';
        }
        const nestedDef = this.definitionByName.get(state.nestedFunctionName.toUpperCase());
        if (!nestedDef) {
            return JSON.stringify({function: state.nestedFunctionName, parameters: {}});
        }
        return JSON.stringify(this.serializeStates(nestedDef, state.nestedParameters ?? []));
    }
}
